import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

function renderMarkdown(text) {
  if (!text) return text;

  const lines = text.split('\n');
  return lines.map((line, lineIdx) => {
    let element = line.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');

    if (element.startsWith('# ')) {
      element = `<strong>${element.substring(2)}</strong>`;
    }

    if (element.startsWith('• ')) {
      element = `&bull; ${element.substring(2)}`;
    }

    return <div key={lineIdx} dangerouslySetInnerHTML={{ __html: element }} />;
  });
}

export default function SalesAgent() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      text: t('salesAgent.greeting', {
        defaultValue: 'Hallo! Ich bin dein TimeLock Sales Guide. Ich helfe dir gern beim passenden Paket.',
      }),
    },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);

  const title = t('salesAgent.title', { defaultValue: 'Sales Guide' });
  const triggerLabel = t('salesAgent.trigger', { defaultValue: 'Chat' });
  const placeholder = t('salesAgent.placeholder', { defaultValue: 'Deine Frage...' });
  const loadingText = t('salesAgent.loading', { defaultValue: 'Wird bearbeitet...' });
  const sendLabel = t('salesAgent.send', { defaultValue: 'Send' });
  const genericError = t('salesAgent.error', {
    defaultValue: 'Fehler. Bitte spaeter erneut versuchen.',
  });
  const connectionError = t('salesAgent.connectionError', {
    defaultValue: 'Verbindungsfehler. Bitte pruefe die Verbindung.',
  });

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const nextInput = input.trim();
    setMessages((prev) => [...prev, { role: 'user', text: nextInput }]);
    setInput('');
    setLoading(true);

    try {
      const response = await fetch('/api/chat/sales', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: nextInput }),
      });

      const data = await response.json();
      if (data.success) {
        setMessages((prev) => [...prev, { role: 'assistant', text: data.reply }]);
      } else {
        setMessages((prev) => [...prev, { role: 'assistant', text: genericError }]);
      }
    } catch (error) {
      console.error('Sales chat error:', error);
      setMessages((prev) => [...prev, { role: 'assistant', text: connectionError }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(!open)}
        className="fixed bottom-6 right-4 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-blue-600 text-xl text-white shadow-lg transition-all hover:from-blue-600 hover:to-blue-700 hover:shadow-xl sm:right-6"
        title={title}
      >
        {triggerLabel}
      </button>

      {open && (
        <div className="fixed bottom-24 left-4 right-4 z-40 flex h-[min(70vh,34rem)] max-h-[34rem] flex-col overflow-hidden rounded-2xl border border-blue-500/30 bg-gradient-to-br from-slate-900 to-slate-800 shadow-2xl sm:left-auto sm:right-6 sm:w-[26rem]">
          <div className="flex items-center justify-between bg-gradient-to-r from-blue-600 to-blue-700 px-5 py-4">
            <h3 className="text-lg font-bold text-white">{title}</h3>
            <button
              onClick={() => setOpen(false)}
              className="rounded px-2 py-1 text-white transition hover:bg-blue-700/50"
            >
              X
            </button>
          </div>

          <div className="flex-1 space-y-4 overflow-y-auto p-4 sm:p-5">
            {messages.map((msg, idx) => (
              <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[85%] rounded-lg px-4 py-3 text-sm leading-relaxed sm:text-base ${
                    msg.role === 'user'
                      ? 'bg-blue-600 text-white'
                      : 'bg-slate-700 text-gray-100'
                  }`}
                >
                  {msg.role === 'user' ? msg.text : renderMarkdown(msg.text)}
                </div>
              </div>
            ))}

            {loading && (
              <div className="flex justify-start">
                <div className="rounded-lg bg-slate-700 px-4 py-3 text-sm text-gray-100 sm:text-base">
                  {loadingText}
                </div>
              </div>
            )}
          </div>

          <div className="flex gap-2 border-t border-slate-700 p-4 sm:p-5">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSend()}
              placeholder={placeholder}
              className="flex-1 rounded bg-slate-800 px-4 py-3 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500 sm:text-base"
              disabled={loading}
            />
            <button
              onClick={handleSend}
              disabled={loading || !input.trim()}
              className="rounded bg-blue-600 px-4 py-3 font-bold text-white transition hover:bg-blue-700 disabled:bg-slate-600"
            >
              {sendLabel}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
